import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, Info } from "lucide-react";

// Target fields, grouped and ordered exactly as the backend/product contract
// expects them to be presented.
export const TARGET_GROUPS = [
  {
    label: "Rider",
    fields: [
      { key: "patient_name", label: "Rider name", joinable: true },
      { key: "member_id", label: "Member / Medicaid ID" },
      { key: "phone", label: "Phone" },
      { key: "dob", label: "Date of birth" },
    ],
  },
  {
    label: "Trip",
    fields: [
      { key: "external_ref", label: "Payer trip ID (for duplicates)" },
      { key: "pickup_datetime", label: "Pickup date and time", datetimeSplit: true },
      { key: "appointment_time", label: "Appointment time" },
      { key: "return_time", label: "Return pickup time (creates the return leg)" },
      { key: "trip_type", label: "Trip type" },
      { key: "mobility", label: "Mobility / level of service" },
      { key: "broker_direction", label: "Broker direction" },
      { key: "fare", label: "Broker fare" },
    ],
  },
  {
    label: "Places",
    fields: [
      { key: "pickup_address", label: "Pickup address", joinable: true },
      { key: "dropoff_address", label: "Dropoff address", joinable: true },
      { key: "dropoff_phone", label: "Destination phone" },
    ],
  },
  {
    label: "Other",
    fields: [
      { key: "payer", label: "Payer" },
      { key: "company_name", label: "Client company (by name)" },
      { key: "con_number", label: "CON # (payer client number)" },
      { key: "notes", label: "Notes" },
    ],
  },
];

const ALL_FIELD_LABELS = {
  patient_name: "Rider name",
  first_name: "First name",
  last_name: "Last name",
  member_id: "Member / Medicaid ID",
  phone: "Phone",
  dob: "Date of birth",
  external_ref: "Payer trip ID (for duplicates)",
  pickup_datetime: "Pickup date and time",
  appointment_time: "Appointment time",
  return_time: "Return pickup time (creates the return leg)",
  trip_type: "Trip type",
  mobility: "Mobility / level of service",
  broker_direction: "Broker direction",
  fare: "Broker fare",
  pickup_address: "Pickup address",
  dropoff_address: "Dropoff address",
  dropoff_phone: "Destination phone",
  payer: "Payer",
  company_name: "Client company (by name)",
  con_number: "CON # (payer client number)",
  notes: "Notes",
};
const SAMPLE_FIELD_ORDER = Object.keys(ALL_FIELD_LABELS);

// Fields whose mapper UI is a plain header dropdown (as opposed to the name/pickup_datetime/join
// special cases below). Any of these can also arrive as a {coalesce:[...]} or {memo:[...]} source
// from the suggester or a saved profile; when it does, renderFieldRow shows a read-only label
// instead of the dropdown and round-trips the object unchanged on save (no editor for those, same
// as the contract asks for join).
const SIMPLE_FIELD_KEYS = [
  "member_id", "phone", "dob", "external_ref", "appointment_time", "return_time",
  "trip_type", "mobility", "broker_direction", "fare", "payer", "company_name",
  "con_number", "notes", "dropoff_phone",
];

const PAYER_OPTIONS = ["UCare", "Blue Plus", "Medica", "HealthPartners", "MA fee-for-service", "MTM", "Avo"];
const TRIP_TYPE_OPTIONS = [
  { value: "one_way", label: "One-way" },
  { value: "round_trip", label: "Round-trip" },
  { value: "multi_leg", label: "Multi-leg" },
];
const MOBILITY_OPTIONS = [
  { value: "ambulatory", label: "Ambulatory" },
  { value: "wheelchair", label: "Wheelchair" },
  { value: "stretcher", label: "Stretcher" },
];

function emptyJoinField() {
  return { mode: "single", value: "", join: [] };
}
function emptyDatetimeField() {
  return { mode: "single", value: "", date: "", time: "" };
}

function joinFieldFromApiSource(src) {
  if (src && typeof src === "object" && Array.isArray(src.join)) {
    return { mode: "join", value: "", join: src.join };
  }
  return { mode: "single", value: typeof src === "string" ? src : "", join: [] };
}
function datetimeFieldFromApi(src) {
  if (src && typeof src === "object" && !Array.isArray(src) && ("date" in src || "time" in src)) {
    return { mode: "split", value: "", date: src.date || "", time: src.time || "" };
  }
  return { mode: "single", value: typeof src === "string" ? src : "", date: "", time: "" };
}
function apiSourceFromJoinField(f) {
  if (f.mode === "join") return f.join.length ? { join: f.join, sep: " " } : null;
  return f.value || null;
}
function apiDatetimeFromField(f) {
  if (f.mode === "split") return f.date || f.time ? { date: f.date || null, time: f.time || null } : null;
  return f.value || null;
}

function defaultFieldMap() {
  const base = {
    patient_name: emptyJoinField(),
    first_name: "",
    last_name: "",
    pickup_datetime: emptyDatetimeField(),
    pickup_address: emptyJoinField(),
    dropoff_address: emptyJoinField(),
  };
  for (const k of SIMPLE_FIELD_KEYS) base[k] = "";
  return base;
}

// Simple fields round-trip whatever the API gave us verbatim: a plain header string, or a
// {coalesce:[...]} / {memo:[...]} object (rendered read-only, see renderFieldRow below). Only the
// name/pickup_datetime/join fields need their own shape-specific parsing.
function fieldMapFromApiMapping(apiMapping) {
  const m = apiMapping || {};
  const d = defaultFieldMap();
  const out = {
    ...d,
    patient_name: joinFieldFromApiSource(m.patient_name),
    first_name: typeof m.first_name === "string" ? m.first_name : "",
    last_name: typeof m.last_name === "string" ? m.last_name : "",
    pickup_datetime: datetimeFieldFromApi(m.pickup_datetime),
    pickup_address: joinFieldFromApiSource(m.pickup_address),
    dropoff_address: joinFieldFromApiSource(m.dropoff_address),
  };
  for (const k of SIMPLE_FIELD_KEYS) out[k] = m[k] ?? "";
  return out;
}

function apiMappingFromFieldMap(fieldMap, nameMode) {
  const out = {
    patient_name: nameMode === "split" ? null : apiSourceFromJoinField(fieldMap.patient_name),
    first_name: nameMode === "split" ? fieldMap.first_name || null : null,
    last_name: nameMode === "split" ? fieldMap.last_name || null : null,
    pickup_datetime: apiDatetimeFromField(fieldMap.pickup_datetime),
    pickup_address: apiSourceFromJoinField(fieldMap.pickup_address),
    dropoff_address: apiSourceFromJoinField(fieldMap.dropoff_address),
  };
  for (const k of SIMPLE_FIELD_KEYS) out[k] = fieldMap[k] || null;
  return out;
}

function rawSingleOrJoin(f, row, headers) {
  if (!f) return "";
  if (f.mode === "join") {
    return f.join
      .map((h) => {
        const idx = headers.indexOf(h);
        return idx >= 0 ? row[idx] ?? "" : "";
      })
      .filter((v) => v !== "")
      .join(" ");
  }
  if (!f.value) return "";
  const idx = headers.indexOf(f.value);
  return idx >= 0 ? row[idx] ?? "" : "";
}
function rawDatetime(f, row, headers) {
  if (!f) return "";
  if (f.mode === "split") {
    const di = f.date ? headers.indexOf(f.date) : -1;
    const ti = f.time ? headers.indexOf(f.time) : -1;
    const d = di >= 0 ? row[di] ?? "" : "";
    const t = ti >= 0 ? row[ti] ?? "" : "";
    return `${d} ${t}`.trim();
  }
  if (!f.value) return "";
  const idx = headers.indexOf(f.value);
  return idx >= 0 ? row[idx] ?? "" : "";
}
function rawSimple(v, row, headers) {
  if (!v) return "";
  const idx = headers.indexOf(v);
  return idx >= 0 ? row[idx] ?? "" : "";
}

function isCoalesceSource(v) {
  return !!(v && typeof v === "object" && !Array.isArray(v) && Array.isArray(v.coalesce));
}
function isMemoSource(v) {
  return !!(v && typeof v === "object" && !Array.isArray(v) && Array.isArray(v.memo));
}
function coalesceLabel(v) {
  return (v.coalesce || []).filter(Boolean).join(", else ");
}
function memoLabel(v) {
  const parts = (v.memo || []).map(([label, header]) => label || header);
  return `Memo: ${parts.join(" + ")}`;
}

function HeaderSelect({ headers, value, onChange, testId }) {
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full text-xs px-2.5 py-1.5 rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-slate-300"
      data-testid={testId}
    >
      <option value="">Not in file</option>
      {headers.map((h) => (
        <option key={h} value={h}>{h}</option>
      ))}
    </select>
  );
}

function JoinPicker({ headers, selected, onChange }) {
  const toggle = (h) => {
    if (selected.includes(h)) onChange(selected.filter((x) => x !== h));
    else onChange([...selected, h]);
  };
  return (
    <div className="border border-slate-200 rounded-md p-2 bg-slate-50 max-h-32 overflow-y-auto">
      {headers.length === 0 && <div className="text-[11px] text-slate-400 italic">No columns in file</div>}
      {headers.map((h) => (
        <label key={h} className="flex items-center gap-2 text-[11px] py-0.5">
          <input type="checkbox" checked={selected.includes(h)} onChange={() => toggle(h)} />
          <span>{h}</span>
          {selected.includes(h) && <span className="text-slate-400">#{selected.indexOf(h) + 1}</span>}
        </label>
      ))}
    </div>
  );
}

export default function ImportMapper({
  headers,
  sampleRows,
  initial,
  resetKey,
  companyId,
  selectedProfile,
  onSaved,
  onChange,
}) {
  const [fieldMap, setFieldMap] = useState(() => fieldMapFromApiMapping(initial?.mapping));
  const [nameMode, setNameMode] = useState(() => (initial?.mapping?.first_name || initial?.mapping?.last_name ? "split" : "single"));
  const [nameOrder, setNameOrder] = useState(initial?.options?.name_order || "first_last");
  const [skipRows, setSkipRows] = useState(initial?.options?.skip_rows || 0);
  const [dateFormat, setDateFormat] = useState(initial?.options?.date_format || "");
  const [timeFormat, setTimeFormat] = useState(initial?.options?.time_format || "");
  const [defaultPayer, setDefaultPayer] = useState(initial?.defaults?.payer || "");
  const [defaultTripType, setDefaultTripType] = useState(initial?.defaults?.trip_type || "");
  const [defaultMobility, setDefaultMobility] = useState(initial?.defaults?.mobility || "");
  const [profileName, setProfileName] = useState(selectedProfile?.name || "");
  const [profileDescription, setProfileDescription] = useState(selectedProfile?.description || "");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  const initializedKey = useRef(null);

  // Reset all local state whenever the caller hands us a new context: a
  // freshly inspected file, or a different saved profile picked in Step 1.
  useEffect(() => {
    if (initializedKey.current === resetKey) return;
    initializedKey.current = resetKey;
    setFieldMap(fieldMapFromApiMapping(initial?.mapping));
    setNameMode(initial?.mapping?.first_name || initial?.mapping?.last_name ? "split" : "single");
    setNameOrder(initial?.options?.name_order || "first_last");
    setSkipRows(initial?.options?.skip_rows || 0);
    setDateFormat(initial?.options?.date_format || "");
    setTimeFormat(initial?.options?.time_format || "");
    setDefaultPayer(initial?.defaults?.payer || "");
    setDefaultTripType(initial?.defaults?.trip_type || "");
    setDefaultMobility(initial?.defaults?.mobility || "");
    setProfileName(selectedProfile?.name || "");
    setProfileDescription(selectedProfile?.description || "");
    setDirty(false);
    setForbidden(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const mutate = (updater) => {
    setFieldMap((prev) => updater(prev));
    setDirty(true);
  };
  const setSimple = (key) => (val) => mutate((prev) => ({ ...prev, [key]: val }));

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  const riderNameValid =
    nameMode === "split"
      ? !!(fieldMap.first_name && fieldMap.last_name)
      : fieldMap.patient_name.mode === "join"
      ? fieldMap.patient_name.join.length > 0
      : !!fieldMap.patient_name.value;
  const pickupAddrValid = fieldMap.pickup_address.mode === "join" ? fieldMap.pickup_address.join.length > 0 : !!fieldMap.pickup_address.value;
  const dropoffAddrValid = fieldMap.dropoff_address.mode === "join" ? fieldMap.dropoff_address.join.length > 0 : !!fieldMap.dropoff_address.value;
  const pickupDtValid =
    fieldMap.pickup_datetime.mode === "split" ? !!(fieldMap.pickup_datetime.date && fieldMap.pickup_datetime.time) : !!fieldMap.pickup_datetime.value;

  useEffect(() => {
    const mapping = apiMappingFromFieldMap(fieldMap, nameMode);
    const options = { name_order: nameOrder, date_format: dateFormat || null, time_format: timeFormat || null, skip_rows: Number(skipRows) || 0 };
    const defaults = { payer: defaultPayer || null, trip_type: defaultTripType || null, mobility: defaultMobility || null, company_id: companyId || null };

    const missing = [];
    if (!riderNameValid) missing.push("Rider name");
    if (!pickupAddrValid) missing.push("Pickup address");
    if (!dropoffAddrValid) missing.push("Dropoff address");
    if (!pickupDtValid) missing.push("Pickup date and time");

    onChangeRef.current?.({ mapping, options, defaults, valid: missing.length === 0, missing });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldMap, nameMode, nameOrder, skipRows, dateFormat, timeFormat, defaultPayer, defaultTripType, defaultMobility, companyId]);

  const fieldHasMapping = (key) => {
    switch (key) {
      case "patient_name":
        return nameMode !== "split" && riderNameValid;
      case "first_name":
        return nameMode === "split" && !!fieldMap.first_name;
      case "last_name":
        return nameMode === "split" && !!fieldMap.last_name;
      case "pickup_datetime":
        return pickupDtValid;
      case "pickup_address":
        return pickupAddrValid;
      case "dropoff_address":
        return dropoffAddrValid;
      default:
        return !!fieldMap[key];
    }
  };

  const sampleValueFor = (key, row) => {
    if (key === "patient_name") return nameMode === "split" ? "" : rawSingleOrJoin(fieldMap.patient_name, row, headers);
    if (key === "first_name" || key === "last_name") return nameMode === "split" ? rawSimple(fieldMap[key], row, headers) : "";
    if (key === "pickup_datetime") return rawDatetime(fieldMap.pickup_datetime, row, headers);
    if (key === "pickup_address" || key === "dropoff_address") return rawSingleOrJoin(fieldMap[key], row, headers);
    return rawSimple(fieldMap[key], row, headers);
  };

  const mappedSampleFields = useMemo(
    () => SAMPLE_FIELD_ORDER.filter((k) => fieldHasMapping(k)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fieldMap, nameMode]
  );

  const isBuiltinSelected = !!selectedProfile?.builtin;

  const doSave = async (asNew) => {
    if (!profileName.trim()) {
      toast.error("Name the mapping profile first");
      return;
    }
    const payload = {
      name: profileName.trim(),
      description: profileDescription.trim(),
      headers,
      mapping: apiMappingFromFieldMap(fieldMap, nameMode),
      defaults: { payer: defaultPayer || null, trip_type: defaultTripType || null, mobility: defaultMobility || null, company_id: companyId || null },
      options: { name_order: nameOrder, date_format: dateFormat || null, time_format: timeFormat || null, skip_rows: Number(skipRows) || 0 },
    };
    setSaving(true);
    try {
      let resp;
      if (selectedProfile && !asNew && !isBuiltinSelected) {
        resp = await api.put(`/import/profiles/${selectedProfile.id}`, payload);
        toast.success("Mapping profile updated");
      } else {
        resp = await api.post("/import/profiles", payload);
        toast.success("Mapping profile saved");
      }
      setDirty(false);
      onSaved?.(resp.data);
    } catch (e) {
      if (e.response?.status === 403) setForbidden(true);
      else toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const renderFieldRow = (f) => {
    if (f.key === "patient_name") {
      return (
        <tr key="patient_name" className="border-t border-slate-100">
          <td className="px-3 py-2 align-top w-56">
            <div className="font-medium text-slate-700">Rider name</div>
            <label className="mt-1 flex items-center gap-1.5 text-[10px] text-slate-500">
              <input
                type="checkbox"
                checked={nameMode === "split"}
                onChange={(e) => {
                  setNameMode(e.target.checked ? "split" : "single");
                  setDirty(true);
                }}
              />
              First name + last name are separate columns
            </label>
          </td>
          <td className="px-3 py-2">
            {nameMode === "split" ? (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[10px] text-slate-500 mb-1">First name</div>
                  <HeaderSelect headers={headers} value={fieldMap.first_name} onChange={setSimple("first_name")} testId="import-map-first_name" />
                </div>
                <div>
                  <div className="text-[10px] text-slate-500 mb-1">Last name</div>
                  <HeaderSelect headers={headers} value={fieldMap.last_name} onChange={setSimple("last_name")} testId="import-map-last_name" />
                </div>
              </div>
            ) : (
              <div>
                <HeaderSelect
                  headers={headers}
                  value={fieldMap.patient_name.value}
                  onChange={(v) => mutate((p) => ({ ...p, patient_name: { ...p.patient_name, mode: "single", value: v } }))}
                  testId="import-map-patient_name"
                />
                <label className="mt-1.5 flex items-center gap-1.5 text-[10px] text-slate-500">
                  <input
                    type="checkbox"
                    checked={fieldMap.patient_name.mode === "join"}
                    onChange={(e) => mutate((p) => ({ ...p, patient_name: { ...p.patient_name, mode: e.target.checked ? "join" : "single" } }))}
                  />
                  Build from several columns
                </label>
                {fieldMap.patient_name.mode === "join" && (
                  <div className="mt-1.5">
                    <JoinPicker
                      headers={headers}
                      selected={fieldMap.patient_name.join}
                      onChange={(j) => mutate((p) => ({ ...p, patient_name: { ...p.patient_name, join: j } }))}
                    />
                  </div>
                )}
              </div>
            )}
          </td>
        </tr>
      );
    }

    if (f.key === "pickup_datetime") {
      return (
        <tr key="pickup_datetime" className="border-t border-slate-100">
          <td className="px-3 py-2 align-top w-56">
            <div className="font-medium text-slate-700">{f.label}</div>
            <label className="mt-1 flex items-center gap-1.5 text-[10px] text-slate-500">
              <input
                type="checkbox"
                checked={fieldMap.pickup_datetime.mode === "split"}
                onChange={(e) => mutate((p) => ({ ...p, pickup_datetime: { ...p.pickup_datetime, mode: e.target.checked ? "split" : "single" } }))}
              />
              Date and time are separate columns
            </label>
          </td>
          <td className="px-3 py-2">
            {fieldMap.pickup_datetime.mode === "split" ? (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[10px] text-slate-500 mb-1">Date column</div>
                  <HeaderSelect
                    headers={headers}
                    value={fieldMap.pickup_datetime.date}
                    onChange={(v) => mutate((p) => ({ ...p, pickup_datetime: { ...p.pickup_datetime, date: v } }))}
                    testId="import-map-pickup_date"
                  />
                </div>
                <div>
                  <div className="text-[10px] text-slate-500 mb-1">Time column</div>
                  <HeaderSelect
                    headers={headers}
                    value={fieldMap.pickup_datetime.time}
                    onChange={(v) => mutate((p) => ({ ...p, pickup_datetime: { ...p.pickup_datetime, time: v } }))}
                    testId="import-map-pickup_time"
                  />
                </div>
              </div>
            ) : (
              <HeaderSelect
                headers={headers}
                value={fieldMap.pickup_datetime.value}
                onChange={(v) => mutate((p) => ({ ...p, pickup_datetime: { ...p.pickup_datetime, value: v } }))}
                testId="import-map-pickup_datetime"
              />
            )}
          </td>
        </tr>
      );
    }

    if (f.joinable) {
      const fv = fieldMap[f.key];
      return (
        <tr key={f.key} className="border-t border-slate-100">
          <td className="px-3 py-2 align-top w-56">
            <div className="font-medium text-slate-700">{f.label}</div>
            <label className="mt-1 flex items-center gap-1.5 text-[10px] text-slate-500">
              <input
                type="checkbox"
                checked={fv.mode === "join"}
                onChange={(e) => mutate((p) => ({ ...p, [f.key]: { ...p[f.key], mode: e.target.checked ? "join" : "single" } }))}
              />
              Build from several columns
            </label>
          </td>
          <td className="px-3 py-2">
            {fv.mode === "join" ? (
              <JoinPicker headers={headers} selected={fv.join} onChange={(j) => mutate((p) => ({ ...p, [f.key]: { ...p[f.key], join: j } }))} />
            ) : (
              <HeaderSelect
                headers={headers}
                value={fv.value}
                onChange={(v) => mutate((p) => ({ ...p, [f.key]: { ...p[f.key], value: v } }))}
                testId={`import-map-${f.key}`}
              />
            )}
          </td>
        </tr>
      );
    }

    const val = fieldMap[f.key];
    return (
      <tr key={f.key} className="border-t border-slate-100">
        <td className="px-3 py-2 align-top w-56">
          <div className="font-medium text-slate-700">{f.label}</div>
        </td>
        <td className="px-3 py-2">
          {isCoalesceSource(val) || isMemoSource(val) ? (
            <div
              className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5"
              data-testid={`import-map-${f.key}-readonly`}
            >
              {isCoalesceSource(val) ? coalesceLabel(val) : memoLabel(val)}
            </div>
          ) : (
            <HeaderSelect headers={headers} value={val} onChange={setSimple(f.key)} testId={`import-map-${f.key}`} />
          )}
        </td>
      </tr>
    );
  };

  return (
    <div data-testid="import-mapper">
      <div className="overflow-x-auto border border-slate-200 rounded-md">
        <table className="w-full text-xs min-w-[560px]">
          <tbody>
            {TARGET_GROUPS.map((group) => (
              <React.Fragment key={group.label}>
                <tr className="bg-slate-100">
                  <td colSpan={2} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.1em] text-slate-500 font-semibold">
                    {group.label}
                  </td>
                </tr>
                {group.fields.map((f) => renderFieldRow(f))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {sampleRows?.length > 0 && mappedSampleFields.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 mb-1.5">Live sample</div>
          <div className="overflow-x-auto border border-slate-200 rounded-md">
            <table className="text-xs min-w-full">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.08em] text-slate-500">
                <tr>
                  <th className="px-2 py-1.5 text-left">#</th>
                  {mappedSampleFields.map((k) => (
                    <th key={k} className="px-2 py-1.5 text-left whitespace-nowrap">{ALL_FIELD_LABELS[k]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sampleRows.slice(0, 3).map((row, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-2 py-1.5 font-mono text-slate-400">{i + 1}</td>
                    {mappedSampleFields.map((k) => (
                      <td key={k} className="px-2 py-1.5 whitespace-nowrap">
                        {sampleValueFor(k, row) || <span className="text-slate-300">(empty)</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="border border-slate-200 rounded-md p-4">
          <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 mb-3">Options</div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Name order</label>
            <select
              value={nameOrder}
              onChange={(e) => {
                setNameOrder(e.target.value);
                setDirty(true);
              }}
              className="mt-1 w-full text-sm px-3 py-2.5 rounded-md border border-slate-200 bg-white"
              data-testid="import-option-name-order"
            >
              <option value="first_last">First Last</option>
              <option value="last_first">Last, First</option>
            </select>
          </div>
          <details className="mt-3">
            <summary className="text-xs text-slate-500 cursor-pointer select-none">Advanced</summary>
            <div className="mt-2 grid grid-cols-1 gap-2">
              <label className="text-xs text-slate-500">
                Skip rows before the header
                <input
                  type="number"
                  min="0"
                  value={skipRows}
                  onChange={(e) => {
                    setSkipRows(e.target.value);
                    setDirty(true);
                  }}
                  className="mt-1 w-full text-sm px-2.5 py-1.5 rounded-md border border-slate-200"
                  data-testid="import-option-skip-rows"
                />
              </label>
              <label className="text-xs text-slate-500">
                Date format (optional, e.g. MM/DD/YYYY)
                <input
                  type="text"
                  value={dateFormat}
                  onChange={(e) => {
                    setDateFormat(e.target.value);
                    setDirty(true);
                  }}
                  className="mt-1 w-full text-sm px-2.5 py-1.5 rounded-md border border-slate-200"
                  placeholder="Auto-detect"
                />
              </label>
              <label className="text-xs text-slate-500">
                Time format (optional, e.g. HH:mm)
                <input
                  type="text"
                  value={timeFormat}
                  onChange={(e) => {
                    setTimeFormat(e.target.value);
                    setDirty(true);
                  }}
                  className="mt-1 w-full text-sm px-2.5 py-1.5 rounded-md border border-slate-200"
                  placeholder="Auto-detect"
                />
              </label>
            </div>
          </details>
        </div>

        <div className="border border-slate-200 rounded-md p-4">
          <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 mb-3">Defaults (used when a row has no value)</div>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Payer</label>
              <input
                list="import-payer-datalist"
                value={defaultPayer}
                onChange={(e) => {
                  setDefaultPayer(e.target.value);
                  setDirty(true);
                }}
                className="mt-1 w-full text-sm px-3 py-2.5 rounded-md border border-slate-200"
                placeholder="e.g. UCare"
                data-testid="import-default-payer"
              />
              <datalist id="import-payer-datalist">
                {PAYER_OPTIONS.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Trip type</label>
                <select
                  value={defaultTripType}
                  onChange={(e) => {
                    setDefaultTripType(e.target.value);
                    setDirty(true);
                  }}
                  className="mt-1 w-full text-sm px-3 py-2.5 rounded-md border border-slate-200 bg-white"
                  data-testid="import-default-trip-type"
                >
                  <option value="">Not set</option>
                  {TRIP_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Mobility</label>
                <select
                  value={defaultMobility}
                  onChange={(e) => {
                    setDefaultMobility(e.target.value);
                    setDirty(true);
                  }}
                  className="mt-1 w-full text-sm px-3 py-2.5 rounded-md border border-slate-200 bg-white"
                  data-testid="import-default-mobility"
                >
                  <option value="">Not set</option>
                  {MOBILITY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-5 border border-slate-200 rounded-md p-4" data-testid="import-profile-save">
        {forbidden ? (
          <div className="text-xs text-slate-500 flex items-center gap-1.5">
            <Info size={13} /> Your role can't save mapping profiles. Ask an agency admin to save this mapping.
          </div>
        ) : (
          <>
            <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 mb-3">Save this mapping</div>
            {isBuiltinSelected && (
              <div className="text-[11px] text-slate-500 mb-2">This is a built-in profile and can't be edited. Save your changes as a new profile.</div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Profile name</label>
                <input
                  type="text"
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  className="mt-1 w-full text-sm px-3 py-2.5 rounded-md border border-slate-200"
                  data-testid="import-profile-name"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Description</label>
                <input
                  type="text"
                  value={profileDescription}
                  onChange={(e) => setProfileDescription(e.target.value)}
                  className="mt-1 w-full text-sm px-3 py-2.5 rounded-md border border-slate-200"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              {selectedProfile && !isBuiltinSelected && dirty ? (
                <>
                  <button
                    type="button"
                    onClick={() => doSave(true)}
                    disabled={saving}
                    className="text-sm px-3 py-2 rounded-md border border-slate-200 hover:border-slate-400 disabled:opacity-60"
                  >
                    Save as new
                  </button>
                  <button type="button" onClick={() => doSave(false)} disabled={saving} className="btn-primary disabled:opacity-60 inline-flex items-center gap-1.5">
                    <Save size={13} /> {saving ? "Updating..." : "Update profile"}
                  </button>
                </>
              ) : (
                <button type="button" onClick={() => doSave(true)} disabled={saving} className="btn-primary disabled:opacity-60 inline-flex items-center gap-1.5">
                  <Save size={13} /> {saving ? "Saving..." : "Save as profile"}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
